import { memo, useState, useCallback, useMemo } from 'react';
import { useQuery, useAction } from 'convex/react';
import { toast } from 'sonner';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { api } from '../../../convex/_generated/api';
import { IPublishDialogProps, IPublishResult } from '@/@types/publishing';
import { Id } from '../../../convex/_generated/dataModel';

interface IActiveAccount {
  _id: Id<'connectedSocialAccounts'>;
  status: string;
  accountName: string;
  totalPublishedPosts: number;
}

const PublishDialog = ({ postId, open, onOpenChange }: IPublishDialogProps) => {
  const [selectedAccountIds, setSelectedAccountIds] = useState<Set<Id<'connectedSocialAccounts'>>>(
    new Set()
  );
  const [isPublishing, setIsPublishing] = useState(false);

  const accounts = useQuery(api.socialMedia.accounts.listConnectedAccounts);
  const publishAction = useAction(api.socialMedia.publishingActions.publishToFacebook);

  const activeAccounts = useMemo(() => {
    return (accounts || []).filter(
      (account: IActiveAccount) => account.status === 'active'
    ) as IActiveAccount[];
  }, [accounts]);

  const hasAccounts = activeAccounts.length > 0;
  const hasSelection = selectedAccountIds.size > 0;

  const handleToggleAccount = useCallback((accountId: Id<'connectedSocialAccounts'>) => {
    setSelectedAccountIds(prev => {
      const next = new Set(prev);
      if (next.has(accountId)) {
        next.delete(accountId);
      } else {
        next.add(accountId);
      }
      return next;
    });
  }, []);

  const handleSelectAll = useCallback(() => {
    if (selectedAccountIds.size === activeAccounts.length) {
      // Deselect all
      setSelectedAccountIds(new Set());
    } else {
      // Select all
      setSelectedAccountIds(
        new Set(activeAccounts.map((a: { _id: Id<'connectedSocialAccounts'> }) => a._id))
      );
    }
  }, [activeAccounts, selectedAccountIds.size]);

  const handlePublish = useCallback(async () => {
    if (!hasSelection) {
      return;
    }

    setIsPublishing(true);

    try {
      const result = await publishAction({
        postId,
        accountIds: Array.from(selectedAccountIds),
      });

      const succeeded = result.results.filter((r: IPublishResult) => r.success).length;
      const failed = result.results.filter((r: IPublishResult) => !r.success).length;

      if (succeeded > 0) {
        const message =
          failed === 0
            ? `Successfully published to ${succeeded} ${succeeded === 1 ? 'page' : 'pages'}`
            : `Published to ${succeeded} ${succeeded === 1 ? 'page' : 'pages'}, failed for ${failed}`;

        toast.success(message);
      }

      if (failed > 0) {
        const failedAccounts = result.results
          .filter((r: IPublishResult) => !r.success)
          .map((r: IPublishResult) => r.accountName)
          .join(', ');

        toast.error(`Failed to publish to: ${failedAccounts}`);
      }

      // Close dialog on success
      if (result.success) {
        onOpenChange(false);
        setSelectedAccountIds(new Set());
      }
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'Failed to publish');
    } finally {
      setIsPublishing(false);
    }
  }, [hasSelection, publishAction, postId, selectedAccountIds, onOpenChange]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Publish to Facebook</DialogTitle>
          <DialogDescription>
            Select which Facebook Pages you want to publish this post to
          </DialogDescription>
        </DialogHeader>

        <div className="py-4">
          {!hasAccounts && (
            <div className="text-center py-8 text-gray-500">
              <p className="mb-2">No connected Facebook Pages</p>
              <p className="text-sm">
                Connect a Facebook Page first from the Connected Accounts page
              </p>
            </div>
          )}

          {hasAccounts && (
            <>
              <div className="flex items-center justify-between mb-4">
                <p className="text-sm text-gray-600">
                  {activeAccounts.length} {activeAccounts.length === 1 ? 'page' : 'pages'} available
                </p>
                <Button variant="ghost" size="sm" onClick={handleSelectAll} disabled={isPublishing}>
                  {selectedAccountIds.size === activeAccounts.length
                    ? 'Deselect All'
                    : 'Select All'}
                </Button>
              </div>

              <div className="space-y-3 max-h-[300px] overflow-y-auto">
                {activeAccounts.map(account => (
                  <div
                    key={account._id}
                    role="button"
                    tabIndex={0}
                    className="flex items-center space-x-3 p-3 border rounded-lg hover:bg-gray-50 cursor-pointer"
                    onClick={() => handleToggleAccount(account._id)}
                    onKeyDown={e => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault();
                        handleToggleAccount(account._id);
                      }
                    }}
                  >
                    <Checkbox
                      checked={selectedAccountIds.has(account._id)}
                      onCheckedChange={() => handleToggleAccount(account._id)}
                      disabled={isPublishing}
                    />
                    <div className="flex-1 min-w-0">
                      <p className="font-medium truncate">{account.accountName}</p>
                      <p className="text-sm text-gray-500">
                        {account.totalPublishedPosts} posts published
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            </>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={isPublishing}>
            Cancel
          </Button>
          <Button onClick={handlePublish} disabled={!hasSelection || isPublishing}>
            {isPublishing
              ? 'Publishing...'
              : `Publish to ${selectedAccountIds.size} ${selectedAccountIds.size === 1 ? 'page' : 'pages'}`}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export const PublishDialogMemo = memo(PublishDialog);
